
// ABOUTME: Individual notification item component with read/unread toggle
// ABOUTME: Displays notification content with appropriate styling and actions

import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  FileText, 
  Settings, 
  Activity,
  Circle,
  CircleCheck
} from 'lucide-react';
import { Notification } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string, isRead: boolean) => void;
}

const NotificationItem = ({ notification, onMarkAsRead }: NotificationItemProps) => {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'account':
        return <User className="w-4 h-4" />;
      case 'referral':
        return <FileText className="w-4 h-4" />;
      case 'system':
        return <Settings className="w-4 h-4" />;
      case 'activity':
        return <Activity className="w-4 h-4" />;
      default:
        return <Circle className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'account':
        return 'bg-blue-100 text-blue-600';
      case 'referral':
        return 'bg-green-100 text-green-600';
      case 'system':
        return 'bg-gray-100 text-gray-600';
      case 'activity':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const timeAgo = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });

  return (
    <div 
      className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        !notification.is_read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Type Icon */}
        <div className={`p-2 rounded-full ${getTypeColor(notification.type)}`}>
          {getTypeIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`text-sm ${notification.is_read ? 'text-gray-700' : 'text-gray-900 font-semibold'}`}>
              {notification.title}
            </h4>
            {!notification.is_read && (
              <Circle className="w-2 h-2 fill-blue-500 text-blue-500" />
            )}
          </div>
          
          <p className={`text-sm mb-2 ${notification.is_read ? 'text-gray-500' : 'text-gray-700'}`}>
            {notification.message}
          </p>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">{timeAgo}</span>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMarkAsRead(notification.id, !notification.is_read)}
              className="h-6 text-xs"
            >
              {notification.is_read ? (
                <>
                  <Circle className="w-3 h-3 mr-1" />
                  Mark unread
                </>
              ) : (
                <>
                  <CircleCheck className="w-3 h-3 mr-1" />
                  Mark read
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
